import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { AppLayout, ProtectedRoute, AdminRoute } from '@/components'
import { BillingGuard } from '@/components/billing'
import SessionMonitor from '@/components/SessionMonitor'
import Analytics from '@/components/Analytics'
import { registerContentPaths } from '@/lib/analytics'
import { LoginPage, RegisterPage, SignupPage, SignupCompletePage, TesterSignupPage, ResetPasswordPage, useAuthStore } from '@/features/auth'
import { DashboardPage } from '@/features/dashboard'
import { CRMPage } from '@/features/crm'
import { QuotesPage } from '@/features/quotes'
import { InvoicesPage } from '@/features/invoices'
import { SchedulingPage } from '@/features/scheduling'
import { JobLogsListPage, JobLogDetailPage } from '@/features/jobLogs'
import { PublicBookingPage, PublicReschedulePage } from '@/features/booking'
import { SettingsPage, ProfileSettingsPage } from '@/features/settings'
import { LineItemsManagerPage } from '@/features/line-items/pages/LineItemsManagerPage'
import { QuoteApprovalPage, QuoteViewPage, InvoiceViewPage, ShortLinkRedirect } from '@/features/publicApproval'
import { ReportsPage } from '@/features/reports'
import {
  LandingPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  AboutPage,
  EmailPolicyPage,
  SmsConsentPage,
  NotFoundPage,
} from '@/features/marketing'
import { ContentPage, CollectionPage, getEntries } from '@/features/content'
import { OnboardingPage, OnboardingManager, AppTourOverlay } from '@/features/onboarding'
import { BillingSuccessPage, BillingCancelledPage } from '@/features/billing/billingPages'
import { QuickBooksCallbackPage } from '@/features/quickbooks'
import { GoogleCalendarCallbackPage } from '@/features/googleCalendar'

// Vertical landing pages sit at the site root, so analytics can't recognise them by prefix the
// way it does /guides/* and /compare/*. Fed from the same registry the routes below are built
// from, so the two can't drift apart.
registerContentPaths(getEntries('solutions').map((entry) => entry.path))

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const ALL_SIDEBAR_ITEMS = [
  { label: 'Dashboard', href: '/app' },
  { label: 'Contacts', href: '/app/crm' },
  { label: 'Quotes', href: '/app/quotes' },
  { label: 'Jobs', href: '/app/job-logs' },
  { label: 'Invoices', href: '/app/invoices' },
  { label: 'Calendar', href: '/app/scheduling' },
  { label: 'Reports', href: '/app/reports' },
  { label: 'Settings', href: '/app/settings' },
] as const

const EMPLOYEE_SIDEBAR_ITEMS = [
  { label: 'Dashboard', href: '/app' },
  { label: 'Jobs', href: '/app/job-logs' },
  { label: 'Calendar', href: '/app/scheduling' },
  { label: 'Profile', href: '/app/profile' },
] as const

function App() {
  const { user, logout, isAuthenticated } = useAuthStore()

  const sidebarItems =
    user?.role === 'employee'
      ? [...EMPLOYEE_SIDEBAR_ITEMS]
      : [...ALL_SIDEBAR_ITEMS]

  const handleLogout = async () => {
    await logout()
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Analytics />
      <SessionMonitor />
      <OnboardingManager />
      <AppTourOverlay />
      <Routes>
        {/* Public Marketing Pages */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/sms-consent" element={<SmsConsentPage />} />
        <Route path="/email-policy" element={<EmailPolicyPage />} />

        {/* MDX marketing content. Routes are static; which articles exist is resolved from
            src/content/<collection>/*.mdx at build time — see features/content/registry.ts. */}
        <Route path="/compare" element={<CollectionPage collection="compare" />} />
        <Route path="/compare/:slug" element={<ContentPage collection="compare" />} />
        <Route path="/guides" element={<CollectionPage collection="guides" />} />
        <Route path="/guides/:slug" element={<ContentPage collection="guides" />} />

        {/* Vertical landing pages live at the site root (/maid-service-software). One explicit
            route each, generated from the manifest — a "/:slug" wildcard here would shadow
            every other top-level route and make the table impossible to reason about. */}
        {getEntries('solutions').map((entry) => (
          <Route
            key={entry.path}
            path={entry.path}
            element={<ContentPage collection="solutions" slug={entry.slug} />}
          />
        ))}

        {/* Public Booking Routes - No authentication required */}
        <Route path="/book" element={<PublicBookingPage />} />
        <Route path="/book/:serviceId" element={<PublicBookingPage />} />

        {/* Public Reschedule - token-secured from confirmation email/SMS */}
        <Route path="/public/booking/:jobId/reschedule" element={<PublicReschedulePage />} />

        {/* Short link redirect (for SMS) */}

        <Route path="/s/:code" element={<ShortLinkRedirect />} />
        {/* Public Approval Routes - No authentication required */}
        <Route path="/public/quote/:id" element={<QuoteViewPage />} />
        <Route path="/public/quote/:id/:action" element={<QuoteApprovalPage />} />
        <Route path="/public/invoice/:id" element={<InvoiceViewPage />} />
        {/* Invoices no longer use Accept/Decline. Old emailed accept/decline links still resolve to
            the invoice view (with Pay Now). Quotes keep their own approval flow above. */}
        <Route path="/public/invoice/:id/:action" element={<InvoiceViewPage />} />

        {/* Auth Routes - Always render, let pages handle redirect */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/signup/complete" element={<SignupCompletePage />} />
        {/* Self-service beta-tester signup (no Stripe/card). Share /auth/tester?code=… as an invite link. */}
        <Route path="/auth/tester" element={<TesterSignupPage />} />
        <Route path="/auth/register" element={<Navigate to="/auth/signup" replace />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* Stripe tester / billing return URLs (public) */}
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancelled" element={<BillingCancelledPage />} />

        {/* QuickBooks OAuth callback (authenticated; exchanges the Intuit code) */}
        <Route
          path="/quickbooks/callback"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <QuickBooksCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* Google Calendar OAuth callback (authenticated; exchanges the Google code) */}
        <Route
          path="/google-calendar/callback"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GoogleCalendarCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* Onboarding Route - Protected but no sidebar */}
        <Route
          path="/app/onboarding"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected App Routes - All under /app */}
        <Route
          path="/app"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                    sidebarItems={sidebarItems}
                    user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                    onLogout={handleLogout}
                  >
                    <DashboardPage />
                  </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/crm"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <CRMPage />
                </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/quotes"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <QuotesPage />
                </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/invoices"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <InvoicesPage />
                </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/line-items"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                    sidebarItems={sidebarItems}
                    user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                    onLogout={handleLogout}
                  >
                    <LineItemsManagerPage />
                  </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/scheduling"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <SchedulingPage />
                </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/job-logs"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <BillingGuard>
                <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <JobLogsListPage />
                </AppLayout>
              </BillingGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/job-logs/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <BillingGuard>
                <AppLayout
                  sidebarItems={sidebarItems}
                  user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                  onLogout={handleLogout}
                >
                  <JobLogDetailPage />
                </AppLayout>
              </BillingGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/profile"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              {user?.role === 'employee' ? (
                <BillingGuard>
                  <AppLayout
                    sidebarItems={sidebarItems}
                    user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                    onLogout={handleLogout}
                  >
                    <ProfileSettingsPage />
                  </AppLayout>
                </BillingGuard>
              ) : (
                <Navigate to="/app/settings" replace />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/reports"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                    sidebarItems={sidebarItems}
                    user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                    onLogout={handleLogout}
                  >
                    <ReportsPage />
                  </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/settings"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AdminRoute userRole={user?.role}>
                <BillingGuard>
                  <AppLayout
                    sidebarItems={sidebarItems}
                    user={user ? { name: user.name, email: user.email, role: user.role } : undefined}
                    onLogout={handleLogout}
                  >
                    <SettingsPage />
                  </AppLayout>
                </BillingGuard>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Unknown routes: signed-in users go back to the app; everyone else gets a real
            noindex 404 page rather than a silent redirect to '/' (which Google reads as a
            soft 404 and can index as duplicate homepage content). */}
        <Route path="*" element={isAuthenticated ? <Navigate to="/app" replace /> : <NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
